import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'app-warranty-returns',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <div class="min-h-screen bg-polished-charcoal text-silk-white py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-4xl mx-auto space-y-8">
        
        <!-- Barra de Navegación Legal Superior (Tabs UX) -->
        <div class="bg-black/40 backdrop-blur-md p-2 rounded-2xl border border-white/10 flex flex-wrap gap-2 justify-center sm:justify-between items-center text-xs sm:text-sm">
          <a routerLink="/" class="text-amethyst-glint hover:text-white px-3 py-2 rounded-xl transition flex items-center gap-1.5 font-medium">
            ← Inicio
          </a>
          <div class="flex flex-wrap gap-1 sm:gap-2">
            <a routerLink="/terminos" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              ⚖️ Términos
            </a>
            <a routerLink="/privacidad" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🔒 Privacidad
            </a>
            <a routerLink="/garantias-retracto" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🛡️ Garantías
            </a>
            <a routerLink="/aviso-legal" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🏛️ Marcas
            </a>
          </div>
          <button (click)="printPage()" class="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-silk-white/80 hover:text-white transition text-xs border border-white/10">
            🖨️ Imprimir
          </button>
        </div>

        <!-- Tarjeta Principal de Contenido -->
        <div class="bg-black/30 backdrop-blur-md rounded-3xl shadow-2xl p-8 sm:p-12 border border-white/10">
          
          <div class="flex items-center justify-between border-b border-white/10 pb-6 mb-8">
            <div>
              <span class="text-xs text-amethyst-glint uppercase tracking-widest font-bold">Estatuto del Consumidor • SIC</span>
              <h1 class="text-3xl sm:text-4xl font-extrabold text-white mt-1 tracking-tight">
                Política de Garantías, Retracto y Devoluciones
              </h1>
            </div>
            <div class="text-right hidden sm:block">
              <p class="text-xs text-silk-white/50">Actualización</p>
              <p class="text-sm font-semibold text-amethyst-glint">Noviembre 2026</p>
            </div>
          </div>

          <p class="text-sm text-silk-white/60 mb-8 bg-white/5 p-4 rounded-xl border border-white/5">
            🛡️ Directrices de la <strong>Superintendencia de Industria y Comercio (SIC)</strong> bajo la <strong>Ley 1480 de 2011</strong>.
          </p>

          <div class="space-y-8 text-silk-white/90 leading-relaxed text-sm sm:text-base">
            
            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>1.</span> Garantía Legal por Defectos de Calidad
              </h2>
              <p class="text-silk-white/80">
                Garantizamos que todos nuestros productos (botellas completas y decants de 5ml/10ml) son 100% originales. La garantía cubre defectos de fábrica, fallas en atomizadores o fugas en el fraccionamiento dentro de los <strong>30 días calendario</strong> siguientes a la entrega.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>2.</span> Derecho de Retracto (Artículo 47 Ley 1480)
              </h2>
              <p class="text-silk-white/80">
                Para compras electrónicas en altairparfum.com, el consumidor puede ejercer el derecho de retracto dentro de los <strong>cinco (5) días hábiles</strong> siguientes a la entrega del bien.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>3.</span> Excepción Sanitaria en Cosméticos y Decants
              </h2>
              <div class="p-5 bg-amber-950/40 rounded-xl border border-amber-500/30 text-amber-200 space-y-2 text-sm">
                <p><strong>⚠️ Restricción Sanitaria Obligatoria:</strong> El derecho de retracto y devolución <strong>NO APLICAN</strong> para perfumes, botellas o decants cuyos sellos de seguridad, celofán de fábrica o empaques protectores hayan sido abiertos, rotos o manipulados.</p>
                <p>Por razones estrictas de <strong>salubridad e higiene</strong>, un producto cosmético o de perfumería abierto no puede ser reingresado al inventario ni comercializado a terceros.</p>
              </div>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>4.</span> Procedimiento de Reclamación
              </h2>
              <p class="text-silk-white/80">Para iniciar una garantía o solicitud:</p>
              <ul class="list-disc pl-6 space-y-2 text-silk-white/80">
                <li>Contáctanos vía correo (contacto&#64;altairparfum.com) o WhatsApp (+57 320 340 5747).</li>
                <li>Adjunta número de orden, datos y evidencia fotográfica o en video del estado del producto.</li>
                <li>Evaluaremos la solicitud en un plazo máximo de 15 días hábiles conforme a la ley.</li>
              </ul>
            </section>

          </div>

          <div class="mt-12 pt-6 border-t border-white/15 flex justify-between items-center">
            <a routerLink="/privacidad" class="text-amethyst-glint hover:text-white text-sm transition">
              ← Anterior: Política de Privacidad
            </a>
            <a routerLink="/aviso-legal" class="text-amethyst-glint hover:text-white text-sm transition">
              Siguiente: Aviso Legal de Marcas →
            </a>
          </div>

        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarrantyReturnsComponent {
  printPage() {
    window.print();
  }
}
